import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/common';
import { subtractDecimals } from '@/utils/decimal';
import { todayCalendarDate } from '@/lib/calendar-date';
import { useCurrencies } from '@/hooks/useCurrencies';
import { NewIncomingFormSchema, type NewIncomingForm } from './types';
import { OperationField, fieldBorderClass, operationInputClass } from './OperationField';

export interface IncomingOperationFormProps {
  clientId: number | undefined;
  onSubmit: (data: NewIncomingForm) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function IncomingOperationForm({
  clientId,
  onSubmit,
  onCancel,
  isSubmitting,
}: IncomingOperationFormProps) {
  const {
    codes: currencyCodes,
    unavailable: currenciesUnavailable,
    error: currencyError,
  } = useCurrencies();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: isValidating },
  } = useForm<NewIncomingForm>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod 3/4 type mismatch with @hookform/resolvers
    resolver: zodResolver(NewIncomingFormSchema as any),
    mode: 'onBlur',
    defaultValues: {
      receivedDate: todayCalendarDate(),
      currencyCode: '',
      amountSent: '',
      amountReceived: '',
      fee: '',
      counterparty: '',
      senderAccount: '',
      senderSwift: '',
      senderBank: '',
      reference: '',
      description: '',
      sameClient: false,
    },
  });

  // The picker sits outside this form, so the selection has to be carried in.
  const currencyCode = watch('currencyCode') ?? '';
  useEffect(() => {
    if (!currencyCodes.includes(currencyCode)) setValue('currencyCode', currencyCodes[0] ?? '');
  }, [currencyCodes, currencyCode, setValue]);

  useEffect(() => {
    setValue('clientId', clientId as number, { shouldValidate: false });
  }, [clientId, setValue]);

  const sameClient = watch('sameClient');
  const amountReceived = watch('amountReceived') ?? '';
  const fee = watch('fee') ?? '';

  // Un frais laisse vide n'est pas un frais nul : la saisie ne nomme alors
  // rien, et c'est la grille tarifaire du client qui tranchera a
  // l'approbation. Annoncer le montant brut comme credit final promettait
  // au client plus que ce qu'il recevra. Tant que l'operateur n'a pas
  // chiffre, l'ecran ne chiffre pas non plus.
  const feeIsNamed = (fee ?? '').trim().length > 0;
  // Shown, never sent: the server recomputes it from the two amounts it is
  // given, so a stale or tampered field here cannot move money.
  const finalCredit = feeIsNamed ? subtractDecimals(amountReceived, fee, 4) : null;

  return (
    <form
      className="mt-6"
      onSubmit={handleSubmit((data) => {
        if (!currenciesUnavailable && currencyCodes.includes(data.currencyCode)) onSubmit(data);
      })}
      data-testid="incoming-operation-form"
    >
      {currencyError && (
        <p role="alert" className="mb-4 text-sm text-red-700">
          Unable to load currencies. Please reload and try again.
        </p>
      )}
      <h4 className="mb-4 text-sm font-semibold text-[#333]">Incoming wire</h4>

      <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
        <OperationField id="inc-date" label="Date" required error={errors.receivedDate?.message}>
          <input
            id="inc-date"
            type="date"
            {...register('receivedDate')}
            className={fieldBorderClass(Boolean(errors.receivedDate))}
            data-testid="incoming-date"
          />
        </OperationField>

        <OperationField
          id="inc-currency"
          label="Currency"
          required
          error={errors.currencyCode?.message}
        >
          <select
            id="inc-currency"
            {...register('currencyCode')}
            disabled={currenciesUnavailable}
            className={fieldBorderClass(Boolean(errors.currencyCode))}
            data-testid="incoming-currency"
          >
            <option value="" disabled>
              {currenciesUnavailable ? 'Currencies unavailable' : 'Select currency'}
            </option>
            {currencyCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </OperationField>

        <OperationField
          id="inc-amount-sent"
          label="Amount sent (initial)"
          error={errors.amountSent?.message}
        >
          <input
            id="inc-amount-sent"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            {...register('amountSent')}
            className={fieldBorderClass(Boolean(errors.amountSent))}
            data-testid="incoming-amount-sent"
          />
        </OperationField>

        <OperationField
          id="inc-amount-received"
          label="Amount received by MTX"
          required
          error={errors.amountReceived?.message}
        >
          <input
            id="inc-amount-received"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            {...register('amountReceived')}
            className={fieldBorderClass(Boolean(errors.amountReceived))}
            data-testid="incoming-amount-received"
          />
        </OperationField>

        <OperationField id="inc-fee" label="Fee (Montelux revenue)" error={errors.fee?.message}>
          <input
            id="inc-fee"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            {...register('fee')}
            className={fieldBorderClass(Boolean(errors.fee))}
            data-testid="incoming-fee"
          />
        </OperationField>

        <OperationField id="inc-final-credit" label="Final credit to client (auto)">
          <input
            id="inc-final-credit"
            type="text"
            readOnly
            disabled
            value={finalCredit ?? ''}
            placeholder={feeIsNamed ? '0.00' : 'Set by the pricing grid'}
            className={`${operationInputClass} border-[#e0e0e0] bg-[#f5f5f5]`}
            data-testid="incoming-final-credit"
          />
          {!feeIsNamed && (
            <p className="mt-1.5 text-xs text-[#999]" data-testid="incoming-fee-from-grid">
              Leave the fee blank and the client&rsquo;s pricing grid sets it at approval.
            </p>
          )}
        </OperationField>

        <OperationField
          id="inc-counterparty"
          label="Counterparty (sender)"
          error={errors.counterparty?.message}
        >
          <input
            id="inc-counterparty"
            type="text"
            maxLength={50}
            disabled={sameClient}
            placeholder={sameClient ? 'The client themselves' : 'Sender name'}
            {...register('counterparty')}
            className={fieldBorderClass(Boolean(errors.counterparty))}
            data-testid="incoming-counterparty"
          />
        </OperationField>

        <OperationField
          id="inc-sender-account"
          label="Account number"
          error={errors.senderAccount?.message}
        >
          <input
            id="inc-sender-account"
            type="text"
            maxLength={50}
            placeholder="e.g. DE89370400440532013000"
            {...register('senderAccount')}
            className={fieldBorderClass(Boolean(errors.senderAccount))}
            data-testid="incoming-sender-account"
          />
        </OperationField>

        <OperationField
          id="inc-sender-swift"
          label="Bank code (SWIFT/BIC)"
          error={errors.senderSwift?.message}
        >
          <input
            id="inc-sender-swift"
            type="text"
            maxLength={50}
            placeholder="e.g. DEUTDEDB"
            {...register('senderSwift')}
            className={fieldBorderClass(Boolean(errors.senderSwift))}
            data-testid="incoming-sender-swift"
          />
        </OperationField>

        <OperationField id="inc-sender-bank" label="Bank name" error={errors.senderBank?.message}>
          <input
            id="inc-sender-bank"
            type="text"
            maxLength={50}
            placeholder="e.g. Deutsche Bank"
            {...register('senderBank')}
            className={fieldBorderClass(Boolean(errors.senderBank))}
            data-testid="incoming-sender-bank"
          />
        </OperationField>

        <OperationField id="inc-reference" label="Reference" error={errors.reference?.message}>
          <input
            id="inc-reference"
            type="text"
            maxLength={50}
            placeholder="e.g. INV-2026-001"
            {...register('reference')}
            className={fieldBorderClass(Boolean(errors.reference))}
            data-testid="incoming-reference"
          />
        </OperationField>

        <OperationField
          id="inc-description"
          label="Description"
          error={errors.description?.message}
        >
          <input
            id="inc-description"
            type="text"
            maxLength={255}
            placeholder="optional"
            {...register('description')}
            className={fieldBorderClass(Boolean(errors.description))}
            data-testid="incoming-description"
          />
        </OperationField>
      </div>

      <label className="mt-4 flex items-center gap-2 text-xs text-[#666]">
        <input
          type="checkbox"
          {...register('sameClient')}
          className="h-3.5 w-3.5 rounded border-[#ccc] accent-[#6B0F1A]"
          data-testid="incoming-same-client"
        />
        Same client (funds received from own external account — no third-party counterparty)
      </label>

      <div className="mt-6 flex items-center gap-3">
        <Button
          type="submit"
          disabled={currenciesUnavailable || isSubmitting || isValidating}
          data-testid="incoming-submit"
        >
          {isSubmitting ? 'Booking…' : 'Book operation'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="incoming-cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}
